import { ALIGNMENTS, ABILITY_NAMES, SKILLS, type Ability } from '@shared/rules/constants'
import { formatModifier } from '@shared/rules/calc'
import { buildCharacter, validateDraft, type CharacterDraft, type DraftIssue } from '@shared/rules/build'
import { CLASS_BUILDS_BY_ID } from '@shared/rules/data/classes'
import { SPECIES_BY_ID } from '@shared/rules/data/species'
import { BACKGROUNDS_BY_ID } from '@shared/rules/data/backgrounds'
import { LANGUAGES } from '@shared/rules/data/equipment'
import { SPELLS_BY_ID } from '@shared/rules/data/spells'
import { Badge, Button, ErrorNote, Input, Panel, Select, Textarea } from '@/components/ui'
import { PickChip, PickCounter, SectionLabel, StepIntro, WitchlightBadge } from './shared'
import type { StepProps } from './StepIdentity'

/** Prompts drawn from the adventure's own opening hook. */
const LOST_THINGS = [
  'My name', 'My memories of home', 'My courage', 'My sense of touch',
  'My shadow', 'My best friend', 'My luck', 'My reflection',
]

const LANGUAGE_PICKS = 2

export function StepDetails({ draft, update }: StepProps) {
  const toggleLanguage = (language: string) => {
    const current = draft.languages
    update({
      languages: current.includes(language)
        ? current.filter((l) => l !== language)
        : current.length < LANGUAGE_PICKS
          ? [...current, language]
          : current,
    })
  }

  return (
    <div className="space-y-6">
      <StepIntro title="Who is this person?">
        The mechanics are done. This last part is the fun bit — a name, a face, and a reason to care.
        None of it is binding, and your DM will happily work with whatever you write.
      </StepIntro>

      <Panel className="space-y-4 p-4">
        <SectionLabel>The basics</SectionLabel>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">Character name</span>
            <Input
              value={draft.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Pip Thistledown"
              maxLength={80}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">Alignment</span>
            <Select value={draft.alignment} onChange={(e) => update({ alignment: e.target.value })}>
              <option value="">Not sure yet</option>
              {ALIGNMENTS.map((value) => <option key={value} value={value}>{value}</option>)}
            </Select>
            <span className="mt-1.5 block text-[11px] text-ink-500">
              A rough moral compass. Leave it blank if you would rather find out in play.
            </span>
          </label>
        </div>
      </Panel>

      <Panel className="p-4">
        <SectionLabel action={<PickCounter chosen={draft.languages.length} total={LANGUAGE_PICKS} noun="chosen" />}>
          Languages
        </SectionLabel>
        <p className="mb-3 text-sm text-ink-400">
          You already speak Common. Pick two more. <span className="text-arcane-400">Sylvan is the
          language of the Feywild</span> and will come up constantly in this campaign.
        </p>
        <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {LANGUAGES.map((language) => (
            <PickChip
              key={language}
              label={language}
              selected={draft.languages.includes(language)}
              disabled={draft.languages.length >= LANGUAGE_PICKS}
              onToggle={() => toggleLanguage(language)}
            />
          ))}
        </div>
      </Panel>

      <Panel className="space-y-4 p-4">
        <SectionLabel>Describe them</SectionLabel>
        <label className="block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">Appearance</span>
          <Textarea
            value={draft.appearance}
            onChange={(e) => update({ appearance: e.target.value })}
            placeholder="What someone notices first. Height, hair, how they dress, anything odd."
            maxLength={2000}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">Personality</span>
          <Textarea
            value={draft.personality}
            onChange={(e) => update({ personality: e.target.value })}
            placeholder="One thing they always do, and one thing they never do."
            maxLength={2000}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">Backstory</span>
          <Textarea
            value={draft.backstory}
            onChange={(e) => update({ backstory: e.target.value })}
            placeholder="A few sentences is plenty. Where are they from, and why are they here?"
            maxLength={6000}
            className="min-h-32"
          />
        </label>
      </Panel>

      <Panel className="p-4">
        <SectionLabel action={<WitchlightBadge />}>What did you lose?</SectionLabel>
        <p className="mb-3 text-sm leading-relaxed text-ink-400">
          This campaign opens with something having been taken from you — years ago, by a bargain
          you barely remember making. It could be an object, but the best answers usually are not.
          Pick one of these or write your own.
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          {LOST_THINGS.map((thing) => (
            <button
              key={thing}
              type="button"
              onClick={() => update({ lostSomething: thing })}
              className={
                'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                (draft.lostSomething === thing
                  ? 'border-arcane-500 bg-arcane-900/60 text-arcane-400'
                  : 'border-ink-600 text-ink-400 hover:border-arcane-500/50 hover:text-ink-200')
              }
            >
              {thing}
            </button>
          ))}
        </div>
        <Input
          value={draft.lostSomething}
          onChange={(e) => update({ lostSomething: e.target.value })}
          placeholder="Or write your own…"
          maxLength={500}
        />
      </Panel>
    </div>
  )
}

// ---------- Review ----------

export interface ReviewProps extends StepProps {
  issues: DraftIssue[]
  onGoToStep: (step: string) => void
  onCreate: () => void
  onDownload: () => void
  creating: boolean
  downloading: boolean
  createdId: string | null
  error: string | null
}

export function StepReview({
  draft, issues, onGoToStep, onCreate, onDownload, creating, downloading, createdId, error,
}: ReviewProps) {
  const built = buildCharacter(draft)
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
  const species = SPECIES_BY_ID.get(draft.speciesId)
  const background = BACKGROUNDS_BY_ID.get(draft.backgroundId)
  const ready = issues.length === 0 && built !== null

  return (
    <div className="space-y-6">
      <StepIntro title={ready ? 'Meet your character' : 'Almost there'}>
        {ready
          ? 'Everything checks out. Create the character to add them to the campaign, then download a filled-in sheet as your backup.'
          : 'A few things still need choosing. Each one below links straight to the step it belongs to.'}
      </StepIntro>

      {issues.length > 0 && (
        <Panel className="border-gold-600/40 p-4">
          <SectionLabel>Still to do</SectionLabel>
          <ul className="space-y-2">
            {issues.map((issue, index) => (
              <li key={`${issue.step}-${index}`}>
                <button
                  type="button"
                  onClick={() => onGoToStep(issue.step)}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100"
                >
                  <span aria-hidden className="text-gold-500">→</span>
                  {issue.message}
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {built && classBuild && species && background && (
        <>
          <Panel className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl">{draft.name || 'Unnamed character'}</h2>
                <p className="mt-1 text-sm text-ink-400">
                  Level 1 {classBuild.name} · {species.name} · {background.name}
                  {draft.alignment && ` · ${draft.alignment}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {species.source === 'wbtw' && <WitchlightBadge />}
                {background.source === 'wbtw' && <WitchlightBadge />}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2.5 sm:grid-cols-6">
              {(Object.keys(ABILITY_NAMES) as Ability[]).map((ability) => (
                <div key={ability} className="stat-slab gap-0.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">{ability}</span>
                  <span className="font-mono text-xl leading-tight tabular-nums text-gold-400">
                    {formatModifier(built.modifiers[ability])}
                  </span>
                  <span className="font-mono text-[11px] text-ink-500">{built.scores[ability]}</span>
                </div>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              <Slab label="Hit points" value={built.hpMax} />
              <Slab label="Armor Class" value={built.armorClass} />
              <Slab label="Speed" value={`${built.speed} ft`} />
              <Slab label="Proficiency" value={formatModifier(built.proficiencyBonus)} />
            </div>
          </Panel>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel className="p-4">
              <SectionLabel>Skills you are proficient in</SectionLabel>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(built.skillProficiencies).map(([skill, level]) => (
                  <Badge key={skill} tone={level === 'expertise' ? 'gold' : 'neutral'}>
                    {SKILLS[skill as keyof typeof SKILLS].name}
                    {level === 'expertise' && ' ××'}
                  </Badge>
                ))}
              </div>

              <SectionLabel>{' '}</SectionLabel>
              <p className="mb-1.5 text-xs uppercase tracking-wider text-ink-500">Saving throws</p>
              <div className="flex flex-wrap gap-1.5">
                {built.saveProficiencies.map((ability) => (
                  <Badge key={ability} tone="moss">{ABILITY_NAMES[ability]}</Badge>
                ))}
              </div>

              <p className="mb-1.5 mt-4 text-xs uppercase tracking-wider text-ink-500">Languages</p>
              <p className="text-sm text-ink-300">{built.languages.join(', ')}</p>
            </Panel>

            <Panel className="p-4">
              <SectionLabel>Attacks</SectionLabel>
              {built.attacks.length === 0 ? (
                <p className="text-sm text-ink-500">No weapons — you took the gold instead.</p>
              ) : (
                <ul className="space-y-1.5">
                  {built.attacks.map((attack) => (
                    <li key={attack.name} className="flex flex-wrap items-baseline gap-2 text-sm">
                      <span className="text-ink-100">{attack.name}</span>
                      <span className="font-mono text-gold-400">{attack.bonus}</span>
                      <span className="font-mono text-ink-400">{attack.damage}</span>
                    </li>
                  ))}
                </ul>
              )}

              {built.spellcasting && (
                <>
                  <p className="mb-1.5 mt-4 text-xs uppercase tracking-wider text-ink-500">Spellcasting</p>
                  <p className="text-sm text-ink-300">
                    {ABILITY_NAMES[built.spellcasting.ability]} · save DC{' '}
                    <span className="font-mono text-gold-400">{built.spellcasting.saveDC}</span> · attack{' '}
                    <span className="font-mono text-gold-400">{formatModifier(built.spellcasting.attackBonus)}</span>
                  </p>
                  <p className="mt-2 text-sm text-ink-400">
                    {[...draft.cantrips, ...draft.spells]
                      .map((id) => SPELLS_BY_ID.get(id)?.name)
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </>
              )}
            </Panel>
          </div>

          <Panel className="p-4">
            <SectionLabel>Equipment</SectionLabel>
            {/* CSS columns, not a grid: the list has to read top-to-bottom so a
                pack's contents stay under the pack that contains them. */}
            <div className="columns-1 gap-6 text-sm text-ink-300 sm:columns-2">
              {built.equipment.map((item, index) => (
                <p
                  key={`${item}-${index}`}
                  className={item.startsWith('  ') ? 'pl-3 text-ink-500' : ''}
                >
                  {item.trim()}
                </p>
              ))}
            </div>
            <p className="mt-3 border-t border-ink-700/60 pt-3 font-mono text-sm text-gold-400">
              {built.coins.gp} gp
            </p>
          </Panel>
        </>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel className="flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <h3 className="font-display text-lg">
            {createdId ? 'Character created' : 'Ready when you are'}
          </h3>
          <p className="mt-1 text-sm text-ink-400">
            {createdId
              ? 'Your character is in the campaign. Download the sheet as a backup you can print or keep offline.'
              : 'This adds the character to the campaign. You can still edit everything afterwards.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" busy={downloading} disabled={!ready} onClick={onDownload}>
            Download sheet (PDF)
          </Button>
          {!createdId && (
            <Button variant="primary" busy={creating} disabled={!ready} onClick={onCreate}>
              Create character
            </Button>
          )}
        </div>
      </Panel>
    </div>
  )
}

function Slab({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat-slab gap-1">
      <span className="font-mono text-xl tabular-nums text-ink-100">{value}</span>
      <span className="text-[10px] uppercase tracking-wider text-ink-500">{label}</span>
    </div>
  )
}

export { validateDraft }
export type { CharacterDraft }
